import { useCallback, useEffect, useRef, useState } from 'react'

import { useVariantLensApiClient } from '../auth/hooks/useVariantLensApiClient'
import type { PaginatedVariantsResponse, Variant } from './types'

export const DEFAULT_PAGE_SIZE = 100

export type UseCaseVariantsResult = {
  variants: Variant[]
  loading: boolean
  /** True only for the very first page (so the table can show a skeleton). */
  initialLoading: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => void
}

/**
 * Cursor-paginated loader for GET /cases/{caseId}/variants.
 *
 * The API supports opaque-cursor pagination plus optional server-side scoping:
 * `region` (contig or contig:start-end) and the categorical `tags` / `filters`
 * (INFO/TAG and FILTER). Pushing the categorical filters to the server means
 * the table reflects the whole-file facet counts instead of just the loaded
 * page (GEN-3149). This hook accumulates pages for the current scope and the UI
 * sorts (and redundantly re-filters) client-side over the loaded set.
 * `loadMore` is idempotent while a request is in flight and a no-op once
 * `next_cursor` is null. Changing caseId, region, tags, filters, or track
 * resets all state and reloads the first page. `track` selects which discovered
 * VCF to query (small-variant vs CNV); omit it for the API's default track.
 * `basePath` (GEN-5495) overrides the resource prefix so the order and
 * benchmark viewers reuse this loader against `/orders/{token}/variants` and
 * `/benchmarks/{set}/{sample}/variants`, which share the query contract; it is
 * a caller-built, already-encoded path with no trailing slash, and defaults to
 * the case path so existing callers are unchanged.
 */
export function useCaseVariants(
  caseId: string | undefined,
  pageSize: number = DEFAULT_PAGE_SIZE,
  region?: string,
  tags?: string[],
  filters?: string[],
  track?: string,
  basePath?: string
): UseCaseVariantsResult {
  const apiClient = useVariantLensApiClient()

  const [variants, setVariants] = useState<Variant[]>([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)

  // Cursor + in-flight guard live in refs so loadMore stays referentially
  // stable and effects don't re-fire on every page.
  const cursorRef = useRef<string | null>(null)
  const inFlightRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)

  // Stable string keys so a new array identity each render doesn't churn the
  // fetch callback; values are canonical tags / charset-limited FILTER tokens,
  // so '|' is a safe join/split delimiter.
  const tagsKey = (tags ?? []).join('|')
  const filtersKey = (filters ?? []).join('|')

  const fetchPage = useCallback(async () => {
    if (!caseId || inFlightRef.current) return
    inFlightRef.current = true
    setLoading(true)
    setError(null)

    const abort = new AbortController()
    abortRef.current = abort

    const params = new URLSearchParams({ page_size: String(pageSize) })
    if (cursorRef.current) params.set('cursor', cursorRef.current)
    if (region) params.set('region', region)
    if (tagsKey) for (const t of tagsKey.split('|')) params.append('tag', t)
    if (filtersKey) for (const f of filtersKey.split('|')) params.append('filter', f)
    if (track) params.set('track', track)
    const base = basePath ?? `/cases/${encodeURIComponent(caseId)}`
    const url = `${base}/variants?${params.toString()}`

    try {
      const response = await apiClient.get(url, undefined, true, abort.signal)
      if (response.status === 404) throw new Error(`Case '${caseId}' has no variants`)
      if (!response.ok) throw new Error(`API returned ${response.status}`)
      const body = (await response.json()) as PaginatedVariantsResponse
      if (abort.signal.aborted) return
      setVariants((prev) => prev.concat(body.variants))
      cursorRef.current = body.next_cursor
      setHasMore(body.next_cursor !== null)
    } catch (e: unknown) {
      if (abort.signal.aborted) return
      setError(e instanceof Error ? e.message : 'Failed to load variants')
      setHasMore(false)
    } finally {
      if (!abort.signal.aborted) {
        setLoading(false)
        setInitialLoading(false)
      }
      inFlightRef.current = false
    }
  }, [apiClient, caseId, pageSize, region, tagsKey, filtersKey, track, basePath])

  // Reset + load first page whenever the case or region changes.
  useEffect(() => {
    abortRef.current?.abort()
    cursorRef.current = null
    inFlightRef.current = false
    setVariants([])
    setError(null)
    setHasMore(true)
    setInitialLoading(true)
    void fetchPage()
    return () => abortRef.current?.abort()
  }, [fetchPage])

  const loadMore = useCallback(() => {
    if (!hasMore || inFlightRef.current) return
    void fetchPage()
  }, [hasMore, fetchPage])

  return { variants, loading, initialLoading, error, hasMore, loadMore }
}
